/* ---------------------------------------------------------------------------
   Seed register.

   PROVENANCE — read this before trusting a number.

   This is a bridge INVENTORY register, not a condition survey. Every record
   carries the geometry/location fields every bridge has (id, name, route, km,
   district, spans, length, deckWidth, carriageway, year, deckRL, hfl) plus the
   fields on a DoR bridge-inventory sheet (division office, abutment/pier type,
   bearings, railings, condition-assessment history, etc).

   All five records below are real, transcribed from as-built General
   Arrangement / Cross Section drawings held in this project folder (see the
   comment above each). Span lengths, deck levels, HFL, deck/carriageway
   widths and superstructure on those are real, but none of the
   inventory-sheet-only fields are recorded on a drawing, so those stay null
   per NO_SHEET rather than being invented.

   The register previously also carried a sixth real record (Chhare Khola-2,
   transcribed from an actual DoR inventory sheet, 38-F036-015) and twenty-one
   synthetic placeholder bridges invented to give the register a realistic
   shape. Both were removed to show only the five bridges there are drawings
   for; that geometry is not preserved anywhere in this codebase.

   COORDINATES have a different provenance from everything else here, and it is
   weaker. They are the pins of five Google Maps links supplied on 2026-08-17,
   read off the resolved URLs — the place marker (`!3d`/`!4d`), not the view
   centre — and rounded to seven decimal places as given. Four of the five
   resolve to a named bridge on the crossing the record describes; Girubari's is
   a dropped pin with no place name. What that makes them is good enough to put a
   marker on a map of Nepal, and NOT a surveyed position: nothing here has been
   checked against a control point, an abutment centreline or the chainage the
   same record carries, and they are precise to about a metre while being
   accurate to whoever dropped the pin. They are deliberately not in the
   inventory-sheet group (NO_SHEET) for that reason: a sheet's `lat`/`lng` would
   be a surveyed figure, and these must not be mistaken for one.

   DECK BEARINGS are weaker still, and are cartography rather than inventory.
   Each is the axis between the endpoints of the OpenStreetMap `bridge=yes` way
   at that crossing, queried once through Overpass on 2026-08-17 and normalised to
   [0°, 180°) — an axis, not a heading. They exist so the map can draw a structure
   at its recorded size and the right angle instead of a dot; they are accurate to
   a degree or two of what a volunteer mapper drew, and to nothing else. Girubari
   has none, because no bridge way is mapped within 900 m of its pin, and it
   therefore gets no footprint rather than a guessed one.

   Two of the four ways disagree with the record they were taken from, and neither
   disagreement has been resolved:

     · BR-SYN-002's way is tagged `bridge:structure=truss` on NH47 (Siddhartha
       Highway), where this record says a PSC box girder on F077 (Chapakot–Rampur
       Link). A truss on a national highway and a box girder on a feeder road are
       not the same structure, so either the supplied pin is on a different bridge
       at Ramdi or the road attribution here is wrong.
     · BR-LMJ-004's way is named "Dumre-Besishahar" (NH25), where this record says
       F045. Likelier an OSM naming difference than a wrong bridge, but unchecked.

   Both the coordinates and the bearings are editable in the app — on the map, by
   dragging and rotating the structure — so a wrong one is meant to be corrected
   rather than lived with. What is stored then carries no record of who moved it or
   why, which is the next thing this register needs if positions start being
   maintained rather than imported.
   --------------------------------------------------------------------------- */

import type { Bridge, Inspection } from './types.ts';

/* AGENCY USED TO BE HERE. It moved to src/tenant/profiles/nepal-dor.ts when this
   application stopped serving one department: an agency block in the Nepal seed
   file is an agency block that a DPWH session would have read. Call `agency()`
   from src/tenant/agency.ts, which returns the block of whichever department is
   in force. */

/** Districts carried in the register, with the code used in asset ids. */
export const DISTRICTS = [
  { name: 'Chitwan', code: 'CHT', province: 'Bagmati' },
  { name: 'Lamjung', code: 'LMJ', province: 'Gandaki' },
  { name: 'Nawalparasi East', code: 'NWE', province: 'Lumbini' },
  { name: 'Syangja', code: 'SYN', province: 'Gandaki' },
] as const;

/** Road codes referenced by the register. */
export const ROADS: Record<string, string> = {
  H05: 'Narayanghat–Mugling Highway',
  F045: 'Besisahar–Paudi Bazaar–Kalimati Road',
  F062: 'Durge Baireni Road',
  F077: 'Chapakot–Rampur Link',
};

/**
 * Every inventory-sheet field a bridge doesn't have real data for. Spread this
 * into a record and override with real fields where a source document exists —
 * keeps every literal below to only the fields it actually has data for.
 */
const NO_SHEET = {
  inventoryCode: null,
  roadLength: null,
  divisionOffice: null,
  overUnder: null,
  loadRestriction: null,
  heightRestriction: null,
  designLoad: null,
  leftBankDistrict: null,
  leftBankVdc: null,
  rightBankDistrict: null,
  rightBankVdc: null,
  spanLength: null,
  abutmentType: null,
  abutmentFoundation: null,
  pierType: null,
  pierFoundation: null,
  bearings: null,
  railings: null,
  movement: null,
  riverTraining: null,
  runningSurface: null,
  roadSigns: null,
  services: null,
  designedBy: null,
  constructedBy: null,
  inspections: [] as Inspection[],
};

/**
 * What a closure here would cost. Null on every record, and separate from
 * NO_SHEET because these are not inventory-sheet fields either.
 *
 * A DoR bridge-inventory sheet records what the structure IS; it carries no
 * traffic count, no detour and no lifeline designation. Neither does a General
 * Arrangement drawing, which is what all five of these records were transcribed
 * from. So there is nothing to put here and nothing is invented — see
 * src/data/consequence.ts, and `GR-12` in ROADMAP.md for why the fields exist
 * before the data does.
 */
const NO_CONSEQUENCE = {
  aadt: null,
  aadtYear: null,
  detourKm: null,
  detourMinutes: null,
  lifeline: null,
};

/**
 * DoR keeps no columns of its own beyond the sheet, so the extras bag is empty.
 *
 * Spread into every record rather than left off, because `Bridge.tenantFields`
 * is deliberately not nullable — see the note on it in src/data/types.ts. An
 * empty object here is the same statement as an empty `extras` list in
 * src/tenant/profiles/nepal-dor.ts, and the two should stay that way: a field
 * appearing in this bag for a DoR record is a field that belongs in a column.
 */
const NO_EXTRAS = { tenantFields: {} as Readonly<Record<string, string | number | null>> };

/* --------------------------- the register --------------------------- */

export const BRIDGES: Bridge[] = [
  /* ===== 1. Transcribed from 01-Paudi Khola-1.pdf ===== */
  {
    id: 'BR-LMJ-004',
    name: 'Paudi Khola Bridge',
    route: 'F045',
    roadName: 'Besisahar–Paudi Bazaar–Kalimati Road',
    km: 12.4,
    district: 'Lamjung',
    province: 'Gandaki',
    river: 'Paudi Khola',
    type: 'RC T-beam, 3 girders',
    spans: 2,
    length: 70.0,
    deckWidth: 11.0,
    carriageway: 7.5,
    year: 2021,
    deckRL: 585.4,
    hfl: 581.1,
    // Map pin: Paudi Bridge / पाउदी पुल
    lat: 28.1111962,
    lng: 84.4281258,
    // Deck axis: OSM way 340538631, "Dumre-Besishahar" (NH25, old F36), 14 m away
    deckBearing: 166.6,
    ...NO_SHEET,
    ...NO_CONSEQUENCE,
    ...NO_EXTRAS,
  },

  /* ===== 2. Transcribed from 02-Girubari.pdf ===== */
  {
    id: 'BR-NWE-011',
    name: 'Girubari Khola Bridge',
    route: 'F062',
    roadName: 'Durge Baireni Road',
    km: 8.6,
    district: 'Nawalparasi East',
    province: 'Lumbini',
    river: 'Girubari Khola',
    type: 'PSC 3-webbed slab-deck, cast in situ',
    spans: 4,
    length: 140.05,
    deckWidth: 11.0,
    carriageway: 7.5,
    year: 2024,
    deckRL: 199.17,
    hfl: 195.17,
    // Map pin: dropped pin, no place name
    lat: 27.651175,
    lng: 84.004092,
    // Deck axis: no OSM bridge way within 900 m of this pin — no axis known
    deckBearing: null,
    ...NO_SHEET,
    ...NO_CONSEQUENCE,
    ...NO_EXTRAS,
  },

  /* ===== 3. Transcribed from 03-Jugedi Khola.pdf ===== */
  {
    id: 'BR-CHT-107',
    name: 'Jugedi Khola Bridge',
    route: 'H05',
    roadName: 'Narayanghat–Mugling Highway',
    km: 6.2,
    district: 'Chitwan',
    province: 'Bagmati',
    river: 'Jugedi Khola',
    type: 'PT single-cell box girder',
    spans: 1,
    length: 60.4,
    deckWidth: 11.0,
    carriageway: 7.5,
    year: 2020,
    deckRL: 198.2,
    hfl: 189.8,
    // Map pin: Jugedi Khola Bridge / जुगेडी पुल
    lat: 27.766456,
    lng: 84.4700539,
    // Deck axis: OSM way 140332530, H05 (NH44), 11 m away; dual carriageway, one axis
    deckBearing: 159.7,
    ...NO_SHEET,
    ...NO_CONSEQUENCE,
    ...NO_EXTRAS,
  },

  /* ===== 4. Transcribed from 04-Seti Dovan.pdf ===== */
  {
    id: 'BR-CHT-108',
    name: 'Seti Dovan Bridge',
    route: 'H05',
    roadName: 'Narayanghat–Mugling Highway',
    km: 14.8,
    district: 'Chitwan',
    province: 'Bagmati',
    river: 'Seti Khola (Seti–Trishuli confluence)',
    type: 'PT single-cell box girder',
    spans: 1,
    length: 65.4,
    deckWidth: 11.0,
    carriageway: 7.5,
    year: 2020,
    deckRL: 262.6,
    hfl: 246.5,
    // Map pin: Seti Dovan Khola Bridge
    lat: 27.8187909,
    lng: 84.4561631,
    // Deck axis: OSM way 671340085, H05 (NH44), tagged beam, 40 m away
    deckBearing: 107.3,
    ...NO_SHEET,
    ...NO_CONSEQUENCE,
    ...NO_EXTRAS,
  },

  /* ===== 5. Transcribed from 05-Kaligandaki.pdf ===== */
  {
    id: 'BR-SYN-002',
    name: 'Kaligandaki River Bridge',
    route: 'F077',
    roadName: 'Chapakot–Rampur Link',
    km: 3.1,
    district: 'Syangja',
    province: 'Gandaki',
    river: 'Kali Gandaki',
    type: 'PT single-cell box girder',
    spans: 3,
    length: 200.0,
    deckWidth: 11.0,
    carriageway: 7.5,
    year: 2023,
    deckRL: 329.76,
    hfl: 323.66,
    // Map pin: Ramdi Kali Gandaki Bridge
    lat: 27.9024572,
    lng: 83.6324854,
    // Deck axis: OSM way 61193245, NH47, tagged TRUSS — see the note below
    deckBearing: 62.2,
    ...NO_SHEET,
    ...NO_CONSEQUENCE,
    ...NO_EXTRAS,
  },
];
